import moment from 'moment';
import {
  TurniExtraInterface,
  TurniExtraItemInterface,
} from '../../../interfaces/extra-turni-point-of-sale';

/**
 * Tipo che rappresenta i tipi di turno possibili
 */
type TurnoType = 'calendar' | 'shift';

/**
 * Tipo che rappresenta il numero dell'intervallo
 */
type RangeNumber = 1 | 2;

/**
 * Tipo per il risultato della validazione
 */
type ErrorExtraHoursResult = Record<
  string,
  {
    shift: {
      range1: boolean;
      range2: boolean;
    };
    calendar: {
      range1: boolean;
      range2: boolean;
    };
  }
>;

/**
 * Controlla tutti gli orari extra e restituisce gli errori
 * @param extraHours - Configurazione degli orari extra
 * @returns Mappa degli errori per ogni chiave di extraHours
 */
export const checkAllExtraHours = (
  extraHours: TurniExtraInterface
): ErrorExtraHoursResult => {
  const errorDefaultHours: ErrorExtraHoursResult = {};
  const turniTypes: TurnoType[] = ['calendar', 'shift'];

  // Itera su tutte le chiavi in extraHours
  for (const extra of Object.keys(extraHours)) {
    errorDefaultHours[extra] = {
      shift: {
        range1: false,
        range2: false,
      },
      calendar: {
        range1: false,
        range2: false,
      },
    };

    // Per ogni tipo di turno, valida entrambi gli intervalli
    for (const turnoType of turniTypes) {
      errorDefaultHours[extra][turnoType].range1 = checkExtraHoursValue(
        1,
        turnoType,
        extraHours[extra]
      );
      errorDefaultHours[extra][turnoType].range2 = checkExtraHoursValue(
        2,
        turnoType,
        extraHours[extra]
      );
    }
  }

  return errorDefaultHours;
};

/**
 * Verifica la validità di un intervallo orario extra
 * @param rangeNumber - Numero dell'intervallo (1 o 2)
 * @param turniType - Tipo di turno ('calendar' o 'shift')
 * @param hours - Configurazione degli orari per calendar e shift
 * @returns true se c'è un errore, false se l'orario è valido
 */
export const checkExtraHoursValue = (
  rangeNumber: RangeNumber,
  turniType: TurnoType,
  hours: {
    shift: TurniExtraItemInterface | null;
    calendar: TurniExtraItemInterface | null;
  }
): boolean => {
  const range = hours[turniType];

  // Se l'intervallo non è definito, non c'è errore
  if (!range) return false;

  // Estrai gli orari di inizio e fine per l'intervallo corrente
  const rangeStart = getTimeFromRange(range, `startTime${rangeNumber}`);
  const rangeEnd = getTimeFromRange(range, `endTime${rangeNumber}`);

  // Se entrambi gli orari sono nulli, non c'è errore
  if (!rangeStart && !rangeEnd) return false;

  // Se uno solo degli orari è nullo, c'è un errore
  if ((rangeStart && !rangeEnd) || (!rangeStart && rangeEnd)) return true;

  // Se l'orario di inizio è uguale o successivo a quello di fine, c'è un errore
  if (rangeStart!.isSameOrAfter(rangeEnd!)) return true;

  // Controlla sovrapposizione con l'altro intervallo
  if (isOverlappingWithOtherRange(range, rangeNumber, rangeStart!, rangeEnd!)) {
    return true;
  }

  // Per i turni di tipo shift, non ci sono altri controlli
  if (turniType === 'shift') return false;

  // Per i turni di calendario, controlla se sono contenuti nei turni shift
  return !isCalendarWithinShift(hours, rangeStart!, rangeEnd!);
};

/**
 * Estrae un orario da un range in base alla chiave specificata
 * @param range - Oggetto contenente gli orari
 * @param key - Chiave dell'orario da estrarre
 * @returns Un oggetto moment se l'orario esiste, altrimenti null
 */
function getTimeFromRange(
  range: TurniExtraItemInterface,
  key: string
): moment.Moment | null {
  return key in range && range[key] ? moment(range[key].toDate()) : null;
}

/**
 * Verifica se l'intervallo si sovrappone con l'altro intervallo dello stesso tipo
 * @param range - Configurazione dell'intervallo
 * @param currentRangeNumber - Numero dell'intervallo corrente
 * @param rangeStart - Orario di inizio
 * @param rangeEnd - Orario di fine
 * @returns true se c'è sovrapposizione, false altrimenti
 */
function isOverlappingWithOtherRange(
  range: TurniExtraItemInterface,
  currentRangeNumber: RangeNumber,
  rangeStart: moment.Moment,
  rangeEnd: moment.Moment
): boolean {
  const otherRangeNumber: RangeNumber = currentRangeNumber === 1 ? 2 : 1;

  const otherRangeStart = getTimeFromRange(
    range,
    `startTime${otherRangeNumber}`
  );
  const otherRangeEnd = getTimeFromRange(range, `endTime${otherRangeNumber}`);

  if (!otherRangeStart || !otherRangeEnd) return false;

  return (
    otherRangeStart.isBetween(rangeStart, rangeEnd, 'minutes', '[)') ||
    otherRangeEnd.isBetween(rangeStart, rangeEnd, 'minutes', '(]')
  );
}

/**
 * Verifica se l'intervallo di calendario è contenuto in un intervallo di turno
 * @param hours - Configurazione degli orari
 * @param calendarStart - Orario di inizio calendario
 * @param calendarEnd - Orario di fine calendario
 * @returns true se l'intervallo calendario è contenuto in un turno, false altrimenti
 */
function isCalendarWithinShift(
  hours: {
    shift: TurniExtraItemInterface | null;
    calendar: TurniExtraItemInterface | null;
  },
  calendarStart: moment.Moment,
  calendarEnd: moment.Moment
): boolean {
  const rangeShift = hours.shift;

  // Se non ci sono turni shift definiti, c'è un errore
  if (!rangeShift) return false;

  // Estrai gli orari per entrambi gli intervalli shift
  const rangeShiftStart1 = getTimeFromRange(rangeShift, 'startTime1');
  const rangeShiftEnd1 = getTimeFromRange(rangeShift, 'endTime1');
  const rangeShiftStart2 = getTimeFromRange(rangeShift, 'startTime2');
  const rangeShiftEnd2 = getTimeFromRange(rangeShift, 'endTime2');

  // Se non ci sono orari shift definiti, c'è un errore
  if (
    !rangeShiftStart1 &&
    !rangeShiftStart2 &&
    !rangeShiftEnd1 &&
    !rangeShiftEnd2
  )
    return false;

  // Verifica se l'intervallo calendario è contenuto nel primo intervallo shift
  if (
    rangeShiftStart1 &&
    rangeShiftEnd1 &&
    calendarStart.isBetween(
      rangeShiftStart1,
      rangeShiftEnd1,
      'minutes',
      '[)'
    ) &&
    calendarEnd.isBetween(rangeShiftStart1, rangeShiftEnd1, 'minutes', '(]')
  ) {
    return true;
  }

  // Verifica se l'intervallo calendario è contenuto nel secondo intervallo shift
  if (
    rangeShiftStart2 &&
    rangeShiftEnd2 &&
    calendarStart.isBetween(
      rangeShiftStart2,
      rangeShiftEnd2,
      'minutes',
      '[)'
    ) &&
    calendarEnd.isBetween(rangeShiftStart2, rangeShiftEnd2, 'minutes', '(]')
  ) {
    return true;
  }

  // Se non è contenuto in nessun intervallo shift, c'è un errore
  return false;
}
